import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Full-screen photo viewer in the manner of the iPhone Photos app (Tim, 30 Sep): black stage, swipe left / right between
 * photos, pinch or double-tap to zoom and drag around a zoomed photo, swipe down to close, tap to hide the controls,
 * thumbnails along the bottom. Arrow keys and Escape on a keyboard. Same on the iPad and the phone.
 */
export function PhotoViewer({ photos, start = 0, onClose, title }: { photos: string[]; start?: number; onClose: () => void; title?: string }) {
  const [idx, setIdx] = useState(Math.min(Math.max(start, 0), photos.length - 1))
  const [chrome, setChrome] = useState(true)
  const [closing, setClosing] = useState(false)
  // drawn state: swipe offset, dismiss offset, zoom
  const [dx, setDx] = useState(0)
  const [dy, setDy] = useState(0)
  const [z, setZ] = useState({ s: 1, x: 0, y: 0 })
  const [anim, setAnim] = useState(false)
  const stage = useRef<HTMLDivElement>(null)
  const g = useRef({
    pts: new Map<number, { x: number; y: number }>(), axis: '' as '' | 'h' | 'v' | 'pan' | 'pinch',
    sx: 0, sy: 0, st: 0, z0: { s: 1, x: 0, y: 0 }, d0: 0, m0: { x: 0, y: 0 }, moved: false, lastTap: 0, lastTapXY: { x: 0, y: 0 }, tapTimer: 0,
  })
  const W = () => stage.current?.clientWidth ?? window.innerWidth
  const H = () => stage.current?.clientHeight ?? window.innerHeight

  const close = useCallback(() => {
    if (closing) return
    setClosing(true)
    window.setTimeout(onClose, 180)
  }, [closing, onClose])
  const go = useCallback((n: number) => {
    const to = Math.min(Math.max(n, 0), photos.length - 1)
    setAnim(true); setDx(0); setZ({ s: 1, x: 0, y: 0 }); setIdx(to)
    window.setTimeout(() => setAnim(false), 260)
  }, [photos.length])

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowRight') go(idx + 1)
      if (e.key === 'ArrowLeft') go(idx - 1)
    }
    window.addEventListener('keydown', k)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', k); document.body.style.overflow = prev }
  }, [idx, go, close])

  // keep a zoomed photo inside the stage
  const clamp = (s: number, x: number, y: number) => {
    const mx = (W() * (s - 1)) / 2, my = (H() * (s - 1)) / 2
    return { s, x: Math.min(mx, Math.max(-mx, x)), y: Math.min(my, Math.max(-my, y)) }
  }

  const down = (e: React.PointerEvent) => {
    const t = g.current
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    t.pts.set(e.pointerId, { x: e.clientX, y: e.clientY })
    setAnim(false)
    if (t.pts.size === 1) {
      t.sx = e.clientX; t.sy = e.clientY; t.st = Date.now(); t.moved = false; t.z0 = z
      t.axis = z.s > 1.01 ? 'pan' : ''
    } else if (t.pts.size === 2) {
      const [a, b] = [...t.pts.values()]
      t.axis = 'pinch'; t.z0 = z; t.d0 = Math.hypot(a.x - b.x, a.y - b.y) || 1
      t.m0 = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      setDx(0); setDy(0)
    }
  }
  const move = (e: React.PointerEvent) => {
    const t = g.current
    if (!t.pts.has(e.pointerId)) return
    t.pts.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (t.axis === 'pinch' && t.pts.size >= 2) {
      const [a, b] = [...t.pts.values()]
      const d = Math.hypot(a.x - b.x, a.y - b.y)
      const s = Math.min(5, Math.max(0.8, t.z0.s * (d / t.d0)))
      const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      // zoom around the point between the fingers, and follow it as it moves
      const r = stage.current!.getBoundingClientRect()
      const cx = t.m0.x - r.left - r.width / 2, cy = t.m0.y - r.top - r.height / 2
      const k = s / t.z0.s
      setZ({ s, x: cx - (cx - t.z0.x) * k + (m.x - t.m0.x), y: cy - (cy - t.z0.y) * k + (m.y - t.m0.y) })
      t.moved = true
      return
    }
    const ddx = e.clientX - t.sx, ddy = e.clientY - t.sy
    if (Math.abs(ddx) > 6 || Math.abs(ddy) > 6) t.moved = true
    if (t.axis === 'pan') { setZ(clamp(t.z0.s, t.z0.x + ddx, t.z0.y + ddy)); return }
    if (!t.axis && t.moved) t.axis = Math.abs(ddx) > Math.abs(ddy) ? 'h' : 'v'
    if (t.axis === 'h') {
      const edge = (idx === 0 && ddx > 0) || (idx === photos.length - 1 && ddx < 0)
      setDx(edge ? ddx / 3 : ddx)
    }
    if (t.axis === 'v') setDy(Math.max(0, ddy) + Math.min(0, ddy) / 4)
  }
  const up = (e: React.PointerEvent) => {
    const t = g.current
    if (!t.pts.has(e.pointerId)) return
    t.pts.delete(e.pointerId)
    if (t.axis === 'pinch') {
      if (t.pts.size === 1) {   // one finger left: carry on as a pan from here
        const [p] = [...t.pts.values()]
        t.sx = p.x; t.sy = p.y; t.z0 = z; t.axis = z.s > 1.01 ? 'pan' : ''
        return
      }
      setAnim(true)
      setZ(z.s < 1.05 ? { s: 1, x: 0, y: 0 } : clamp(Math.min(z.s, 4), z.x, z.y))
      t.axis = ''
      return
    }
    if (t.pts.size > 0) return
    const dt = Math.max(1, Date.now() - t.st)
    if (t.axis === 'h') {
      const v = dx / dt
      if (dx < -W() * 0.18 || v < -0.45) go(idx + 1)
      else if (dx > W() * 0.18 || v > 0.45) go(idx - 1)
      else { setAnim(true); setDx(0) }
    } else if (t.axis === 'v') {
      if (dy > 110 || dy / dt > 0.6) close()
      else { setAnim(true); setDy(0) }
    } else if (!t.moved) {
      // a tap: two quick taps zoom in at that point (or back out); one tap shows or hides the controls
      const now = Date.now()
      const near = Math.hypot(e.clientX - t.lastTapXY.x, e.clientY - t.lastTapXY.y) < 30
      if (now - t.lastTap < 280 && near) {
        window.clearTimeout(t.tapTimer); t.lastTap = 0
        setAnim(true)
        if (z.s > 1.01) setZ({ s: 1, x: 0, y: 0 })
        else {
          const r = stage.current!.getBoundingClientRect()
          const cx = e.clientX - r.left - r.width / 2, cy = e.clientY - r.top - r.height / 2
          setZ(clamp(2.5, -cx * 1.5, -cy * 1.5))
        }
      } else {
        t.lastTap = now; t.lastTapXY = { x: e.clientX, y: e.clientY }
        t.tapTimer = window.setTimeout(() => setChrome((c) => !c), 280)
      }
    }
    t.axis = ''
  }
  const wheel = (e: React.WheelEvent) => {   // trackpad pinch on a Mac arrives as ctrl + wheel
    if (!e.ctrlKey) return
    e.preventDefault()
    setZ((p) => { const s = Math.min(5, Math.max(1, p.s * (1 - e.deltaY / 100))); return s <= 1.01 ? { s: 1, x: 0, y: 0 } : clamp(s, p.x, p.y) })
  }

  const fade = 1 - Math.min(dy / 400, 0.7)
  const shrink = 1 - Math.min(dy / 1500, 0.2)
  return createPortal(
    <div className={'pv' + (closing ? ' pv--closing' : '')} role="dialog" aria-modal="true" aria-label={title ? `Photos, ${title}` : 'Photos'} style={{ background: `rgba(0,0,0,${fade})` }}>
      <div className={'pv__bar' + (chrome && dy === 0 ? '' : ' pv__bar--hide')}>
        <button type="button" className="pv__btn" onClick={close} aria-label="Close photos">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
        <div className="pv__count">{title && <span className="pv__title">{title}</span>}<span>{idx + 1} of {photos.length}</span></div>
        <span className="pv__btn pv__btn--ghost" aria-hidden="true" />
      </div>
      <div className="pv__stage" ref={stage} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onWheel={wheel}>
        <div className={'pv__track' + (anim ? ' pv__track--anim' : '')} style={{ transform: `translate3d(${-idx * 100}%,0,0) translate3d(${dx}px,${dy}px,0)` }}>
          {photos.map((src, i) => (
            <div key={src + i} className="pv__slide" style={{ left: `${i * 100}%` }}>
              {Math.abs(i - idx) <= 2 && (
                <img src={src} alt={`Photo ${i + 1} of ${photos.length}`} draggable={false} decoding="async"
                  className={'pv__img' + (anim ? ' pv__img--anim' : '')}
                  style={i === idx ? { transform: `translate3d(${z.x}px,${z.y}px,0) scale(${z.s * shrink})` } : undefined} />
              )}
            </div>
          ))}
        </div>
      </div>
      {photos.length > 1 && (
        <div className={'pv__thumbs' + (chrome && dy === 0 && z.s <= 1.01 ? '' : ' pv__thumbs--hide')}>
          {photos.map((src, i) => (
            <button key={src + i} type="button" className={'pv__thumb' + (i === idx ? ' on' : '')} onClick={() => go(i)} aria-label={`Photo ${i + 1}`} aria-current={i === idx ? 'true' : undefined}>
              <img src={src} alt="" draggable={false} />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  )
}
